import * as THREE from "three";
import { glContexts } from "./glContexts";

/**
 * M44 debug-only performance probe (`window.__td.perf`, `?debug` builds only; installed before anything else runs so
 * it sees every loop and timer). Kevin: "frame rate is dropping a lot after the base is attacked … make sure we
 * aren't leaking or multiple rendering things". It samples once a second — or on demand (`sample()`) — what can pile up:
 *  - every WebGLRenderer.render call per frame, by renderer and by scene (a second loop drawing the same scene, a
 *    hidden stage still drawing), and the main renderer's draw calls / triangles / geometries / textures / programs;
 *  - the main scene graph: objects by type (Mesh, SkinnedMesh, InstancedMesh with its live `count`, Points, Sprite,
 *    Line, lights, groups), how many are visible, and the lights that really light (visible, intensity > 0);
 *  - requestAnimationFrame callbacks that ran in the last frame, by where they were registered (one per live loop);
 *  - live setInterval timers and pending setTimeouts, by where they were made;
 *  - window / document event listeners by type; DOM nodes; running Web Animations; audio sources playing;
 *  - live WebGL contexts (infra/glContexts.ts), canvases in the document, JS heap (Chromium), frame times.
 * The wrappers cost a stack capture per registration only while `tracking` (the probe's `start()`); otherwise they
 * just count. Nothing here runs in a normal player build.
 */

type Counts = Record<string, number>;
interface RendererStat { id: number; label: string; renders: number; scenes: Counts }

export interface PerfSample {
  t: number;
  label: string;
  fps: number;
  frameMsMed: number;
  frameMsMax: number;
  calls: number;
  triangles: number;
  geometries: number;
  textures: number;
  programs: number;
  objects: number;
  visible: number;
  byType: Counts;
  instanced: number;
  lights: number;
  litLights: number;
  /** WebGLRenderer.render calls per frame (mean over the window), by renderer label, and by renderer → scene. */
  rendersPerFrame: Counts;
  /** GL draw calls per frame (mean), by canvas ("main", then canvas2, … in the order they first drew). */
  drawsPerFrame: Counts;
  scenesPerFrame: Counts;
  rafLoops: number;
  rafBySite: Counts;
  intervals: number;
  intervalsBySite: Counts;
  timeouts: number;
  listeners: Counts;
  listenersTotal: number;
  dom: number;
  animations: number;
  audioSources: number;
  contexts: string[];
  canvases: number;
  heapMb: number | null;
}

const site = (skip = 3): string => {
  const s = new Error().stack?.split("\n") ?? [];
  for (let i = skip; i < s.length; i++) {
    const l = s[i];
    if (l.includes("perfProbe")) continue;
    const m = /(?:at\s+)?(?:(\S+)\s+\()?(?:.*\/)?([^/\s)]+?):(\d+):\d+\)?$/.exec(l.trim());
    return m ? `${m[1] ?? "?"}@${m[2]}:${m[3]}` : l.trim().slice(0, 80);
  }
  return "?";
};

class PerfProbe {
  tracking = false;
  readonly samples: PerfSample[] = [];
  label = "";
  private rafThisFrame: Counts = {};
  private rafLastFrame: Counts = {};
  private readonly intervals = new Map<number, string>();
  private readonly timeouts = new Map<number, string>();
  private readonly listeners = new Map<EventTarget, Map<string, number>>();
  private audioSources = 0;
  private frameTimes: number[] = [];
  private lastFrameT = 0;
  private frames = 0;
  private readonly renderers = new Map<THREE.WebGLRenderer, RendererStat>();
  private nextRenderer = 1;
  private timer = 0;
  main: { renderer: THREE.WebGLRenderer; scene: THREE.Scene } | null = null;
  private realSetInterval = window.setInterval.bind(window);
  private realClearInterval = window.clearInterval.bind(window);

  install(): void {
    const w = window as unknown as Record<string, unknown>;
    const raf = window.requestAnimationFrame.bind(window);
    // rAF: count callbacks per frame by their registration site; one site re-registering each frame = one live loop
    w.requestAnimationFrame = (cb: FrameRequestCallback): number => {
      const where = this.tracking ? site() : "*";
      return raf((t) => {
        if (t !== this.lastFrameT) this.onFrame(t);
        this.rafThisFrame[where] = (this.rafThisFrame[where] ?? 0) + 1;
        cb(t);
      });
    };
    const si = window.setInterval.bind(window), ci = window.clearInterval.bind(window);
    w.setInterval = (h: TimerHandler, ms?: number, ...a: unknown[]): number => {
      const id = si(h, ms, ...a);
      this.intervals.set(id, this.tracking ? site() : "*");
      return id;
    };
    w.clearInterval = (id?: number): void => { if (id !== undefined) this.intervals.delete(id); ci(id); };
    const st = window.setTimeout.bind(window), ct = window.clearTimeout.bind(window);
    w.setTimeout = (h: TimerHandler, ms?: number, ...a: unknown[]): number => {
      const where = this.tracking ? site() : "*";
      let id = 0;
      const run = typeof h === "function" ? (...x: unknown[]) => { this.timeouts.delete(id); (h as (...y: unknown[]) => void)(...x); } : h;
      id = st(run, ms, ...a);
      this.timeouts.set(id, where);
      return id;
    };
    w.clearTimeout = (id?: number): void => { if (id !== undefined) this.timeouts.delete(id); ct(id); };
    for (const target of [window, document] as EventTarget[]) {
      const add = target.addEventListener.bind(target), rem = target.removeEventListener.bind(target);
      const m = new Map<string, number>();
      this.listeners.set(target, m);
      target.addEventListener = (type: string, l: EventListenerOrEventListenerObject | null, o?: boolean | AddEventListenerOptions) => {
        if (l) m.set(type, (m.get(type) ?? 0) + 1);
        add(type, l, o);
      };
      target.removeEventListener = (type: string, l: EventListenerOrEventListenerObject | null, o?: boolean | EventListenerOptions) => {
        if (l) m.set(type, Math.max(0, (m.get(type) ?? 0) - 1));
        rem(type, l, o);
      };
    }
    // audio sources playing: start → +1, ended → −1
    const Src = (window as unknown as { AudioScheduledSourceNode?: { prototype: AudioScheduledSourceNode } }).AudioScheduledSourceNode;
    if (Src) {
      const start = Src.prototype.start;
      const self = this;
      Src.prototype.start = function (this: AudioScheduledSourceNode, ...a: [number?, number?, number?]) {
        self.audioSources++;
        this.addEventListener("ended", () => { self.audioSources--; }, { once: true });
        return (start as (...x: unknown[]) => void).apply(this, a);
      } as AudioScheduledSourceNode["start"];
    }
    // every draw call, by canvas (three's WebGLRenderer.render is an instance property, so a prototype wrap would see
    // nothing): a second context drawing while hidden, or the main canvas drawn twice a frame, shows up here
    const self = this;
    for (const C of [window.WebGL2RenderingContext, window.WebGLRenderingContext]) {
      if (!C) continue;
      for (const f of ["drawElements", "drawArrays", "drawElementsInstanced", "drawArraysInstanced"] as const) {
        const proto = C.prototype as unknown as Record<string, (...a: unknown[]) => unknown>;
        const o = proto[f];
        if (typeof o !== "function") continue;
        proto[f] = function (this: WebGL2RenderingContext, ...a: unknown[]) {
          const c = this.canvas as HTMLCanvasElement;
          let k = self.canvasLabels.get(c);
          if (!k) { k = `canvas${self.canvasLabels.size + 1}(${c.parentElement?.className || c.className || "detached"})`.slice(0, 48); self.canvasLabels.set(c, k); }
          self.drawsBy[k] = (self.drawsBy[k] ?? 0) + 1;
          return o.apply(this, a);
        };
      }
    }
  }

  private readonly canvasLabels = new Map<HTMLCanvasElement | OffscreenCanvas, string>();
  private drawsBy: Counts = {};

  private onFrame(t: number): void {
    if (this.lastFrameT > 0) this.frameTimes.push(t - this.lastFrameT);
    this.lastFrameT = t;
    this.frames++;
    this.rafLastFrame = this.rafThisFrame;
    this.rafThisFrame = {};
  }

  /** Name the main renderer and scene (the rig), so the report can tell its renders from the side stages'. */
  setMain(renderer: THREE.WebGLRenderer, scene: THREE.Scene): void {
    this.main = { renderer, scene };
    this.canvasLabels.set(renderer.domElement, "main");
    this.wrapRenderer(renderer, "main");
  }

  /** Counts `renderer.render` calls per frame, by scene (the main rig, a side stage the probe is told about). */
  wrapRenderer(renderer: THREE.WebGLRenderer, label: string): void {
    if (this.renderers.has(renderer)) return;
    const r: RendererStat = { id: this.nextRenderer++, label, renders: 0, scenes: {} };
    this.renderers.set(renderer, r);
    const render = renderer.render.bind(renderer);
    renderer.render = (scene: THREE.Object3D, camera: THREE.Camera) => {
      r.renders++;
      const key = scene === this.main?.scene ? "scene" : scene.name || `${scene.type}#${scene.id}`;
      r.scenes[key] = (r.scenes[key] ?? 0) + 1;
      render(scene, camera);
    };
  }

  start(everyMs = 1000): void {
    this.tracking = true;
    this.samples.length = 0;
    this.reset();
    this.realClearInterval(this.timer);
    this.timer = this.realSetInterval(() => { this.samples.push(this.sample()); }, everyMs);
  }

  stop(): PerfSample[] { this.realClearInterval(this.timer); this.timer = 0; this.tracking = false; return this.samples; }

  private reset(): void {
    this.frameTimes = [];
    this.frames = 0;
    for (const r of this.renderers.values()) { r.renders = 0; r.scenes = {}; }
    this.drawsBy = {};
  }

  /** One reading of everything above; the per-frame figures are means over the frames since the last reading. */
  sample(): PerfSample {
    const frames = Math.max(1, this.frames);
    const ft = this.frameTimes.slice().sort((a, b) => a - b);
    const med = ft.length ? ft[Math.floor(ft.length / 2)] : 0;
    const rendersPerFrame: Counts = {}, scenesPerFrame: Counts = {};
    for (const r of this.renderers.values()) {
      if (r.renders) rendersPerFrame[r.label] = +(r.renders / frames).toFixed(2);
      for (const [k, v] of Object.entries(r.scenes)) scenesPerFrame[`${r.label}:${k}`] = +(v / frames).toFixed(2);
    }
    const byType: Counts = {};
    let objects = 0, visible = 0, instanced = 0, lights = 0, lit = 0;
    const main = this.main;
    if (main) {
      main.scene.traverse((o) => {
        objects++;
        let shown = o.visible;
        for (let p = o.parent; shown && p; p = p.parent) shown = p.visible;
        if (shown) visible++;
        const k = (o as THREE.InstancedMesh).isInstancedMesh ? "InstancedMesh" : o.type;
        byType[k] = (byType[k] ?? 0) + 1;
        if ((o as THREE.InstancedMesh).isInstancedMesh) instanced += (o as THREE.InstancedMesh).count;
        if ((o as THREE.Light).isLight) { lights++; if (shown && (o as THREE.Light).intensity > 0) lit++; }
      });
    }
    const info = main?.renderer.info;
    const listeners: Counts = {};
    let listenersTotal = 0;
    for (const [target, m] of this.listeners) {
      const name = target === window ? "window" : "document";
      for (const [type, n] of m) if (n > 0) { listeners[`${name}.${type}`] = n; listenersTotal += n; }
    }
    const bySite = (m: Map<number, string>): Counts => { const c: Counts = {}; for (const v of m.values()) c[v] = (c[v] ?? 0) + 1; return c; };
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    const s: PerfSample = {
      t: Math.round(performance.now()),
      label: this.label,
      fps: ft.length ? +(1000 / med).toFixed(1) : 0,
      frameMsMed: +med.toFixed(1),
      frameMsMax: +(ft[ft.length - 1] ?? 0).toFixed(1),
      calls: info?.render.calls ?? 0,
      triangles: info?.render.triangles ?? 0,
      geometries: info?.memory.geometries ?? 0,
      textures: info?.memory.textures ?? 0,
      programs: info?.programs?.length ?? 0,
      objects, visible, byType, instanced, lights, litLights: lit,
      rendersPerFrame, scenesPerFrame,
      drawsPerFrame: Object.fromEntries(Object.entries(this.drawsBy).map(([k, v]) => [k, +(v / frames).toFixed(1)])),
      rafLoops: Object.values(this.rafLastFrame).reduce((a, b) => a + b, 0),
      rafBySite: { ...this.rafLastFrame },
      intervals: this.intervals.size,
      intervalsBySite: bySite(this.intervals),
      timeouts: this.timeouts.size,
      listeners, listenersTotal,
      dom: document.getElementsByTagName("*").length,
      animations: typeof document.getAnimations === "function" ? document.getAnimations().length : 0,
      audioSources: this.audioSources,
      contexts: glContexts.labels(),
      canvases: document.getElementsByTagName("canvas").length,
      heapMb: mem ? +(mem.usedJSHeapSize / 1048576).toFixed(1) : null,
    };
    this.reset();
    return s;
  }

  /** Pending setTimeouts by registration site (only those made while tracking carry a site). */
  timeoutsBySite(): Counts { const c: Counts = {}; for (const v of this.timeouts.values()) c[v] = (c[v] ?? 0) + 1; return c; }
}

export const perfProbe = new PerfProbe();

/** Installs the wrappers. Call once, before the game is created (main.ts, ?debug only). */
export function installPerfProbe(): PerfProbe {
  perfProbe.install();
  return perfProbe;
}
